import { useTranslation } from 'react-i18next';

import type { CuratorGroup, CuratorRosterRow } from '@/api/curatorGroups';
import { Button } from '@/components/ui/Button';
import { Card, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Pill } from '@/components/ui/Pill';
import { ownership, rowBadge, type RosterTab } from '@/lib/curatorGroupRows';
import { useAuthStore } from '@/stores/auth';
import { cn } from '@/lib/utils';
import { MSection, MSegmented, MTag } from '@/components/mobile/MobileKit';

/**
 * Ученики курса: вкладки, поиск, выбор пачкой (D40, договор §2 и §4).
 *
 * ⚠️ ОКНО ОТРИСОВКИ, А НЕ ПАГИНАЦИЯ. Сервер отдаёт весь курс одним ответом —
 * в PRE-UPPER это 554 строки, и рисовать их разом незачем. Поэтому показываем
 * первые N, а «Выбрать всех найденных» работает по ВСЕМУ отфильтрованному
 * списку: иначе куратор думает, что выбрал 554, а уходит 50.
 */
export function RosterCard({
  rows,
  filtered,
  shown,
  counts,
  tab,
  query,
  selected,
  myGroups,
  targetGroupId,
  loading,
  assigning,
  removing,
  message,
  error,
  onTab,
  onQuery,
  onToggle,
  onSelectAll,
  onClearSelection,
  onShowMore,
  onTargetGroup,
  onAssign,
  onRemove,
  phone = false,
}: {
  /** Телефон (§4.9, правило 8): строки с галочкой слева, полоса «Выбрано N» прибита над нижним меню. */
  phone?: boolean;
  rows: CuratorRosterRow[];
  filtered: CuratorRosterRow[];
  shown: number;
  counts: { all: number; noGroup: number; mine: number };
  tab: RosterTab;
  query: string;
  selected: ReadonlySet<string>;
  myGroups: CuratorGroup[];
  targetGroupId: string;
  loading: boolean;
  assigning: boolean;
  removing: boolean;
  message: string | null;
  error: string | null;
  onTab: (tab: RosterTab) => void;
  onQuery: (q: string) => void;
  onToggle: (enrollmentId: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onShowMore: () => void;
  onTargetGroup: (groupId: string) => void;
  onAssign: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const meId = useMeId();
  const visible = filtered.slice(0, shown);

  const tabs: { key: RosterTab; label: string }[] = [
    { key: 'all', label: t('curatorGroups.tabAll', { count: counts.all }) },
    { key: 'noGroup', label: t('curatorGroups.tabNoGroup', { count: counts.noGroup }) },
    { key: 'mine', label: t('curatorGroups.tabMine', { count: counts.mine }) },
  ];

  if (phone) {
    return (
      // Под прибитой полосой выбора — запас снизу, иначе она закрыла бы последние строки и «Показать ещё».
      <div className={cn('space-y-2', selected.size > 0 && 'pb-44')}>
        <MSection title={t('curatorGroups.rosterTitle')} />
        <MSegmented tabs={tabs} active={tab} onChange={onTab} />
        <Input
          data-roster-search
          placeholder={t('curatorGroups.searchPlaceholder')}
          value={query}
          onChange={(e) => onQuery(e.target.value)}
        />
        <div className="flex items-center justify-between gap-2 px-0.5 text-[12px] text-on-surface-variant">
          <span>{t('curatorGroups.shownOf', { shown: visible.length, total: filtered.length })}</span>
          {/* Берёт весь отфильтрованный список, а не видимые строки (см. выше). */}
          <Button size="md" variant="ghost" onClick={onSelectAll} disabled={!filtered.length}>
            {t('curatorGroups.selectAll')}
          </Button>
        </div>
        {loading ? (
          <p className="px-0.5 text-[13px] text-on-surface-variant">{t('common.loading')}</p>
        ) : !rows.length || !filtered.length ? (
          <p className="px-0.5 text-[13px] text-on-surface-variant">{t('curatorGroups.rosterEmpty')}</p>
        ) : (
          <>
            <ul className="rounded-[26px] bg-surface-container-low px-4">
              {visible.map((r, i) => {
                const badge = rowBadge(r);
                const own = ownership(r, meId);
                const where =
                  badge.kind === 'group'
                    ? t('curatorGroups.badgeGroup', { name: badge.name })
                    : badge.kind === 'curator'
                      ? t('curatorGroups.badgePersonal', { name: badge.name })
                      : t('curatorGroups.badgeNoGroup');
                const whose =
                  own === 'mine' ? t('curatorGroups.ownMine') : own === 'free' ? t('curatorGroups.ownFree') : t('curatorGroups.ownOther');
                return (
                  <li
                    key={r.enrollment_id}
                    data-roster={r.enrollment_id}
                    className={cn(i < visible.length - 1 && 'border-b border-outline')}
                  >
                    {/* Вся строка — подпись галочки: касание по имени отмечает так же, как по квадрату (44 px). */}
                    <label className="flex min-h-11 cursor-pointer items-center gap-3 py-3">
                      <input
                        type="checkbox"
                        className="h-5 w-5 flex-none accent-primary"
                        checked={selected.has(r.enrollment_id)}
                        onChange={() => onToggle(r.enrollment_id)}
                        aria-label={r.student_name}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[14px] font-semibold text-on-surface">{r.student_name}</span>
                        <span className="block truncate text-[11.5px] text-on-surface-variant">
                          {[
                            where,
                            r.access_until
                              ? t('curatorGroups.accessUntil', { date: new Date(r.access_until).toLocaleDateString('ru-RU') })
                              : '',
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </span>
                      <MTag tone={own === 'mine' ? 'info' : own === 'free' ? 'muted' : 'warm'}>{whose}</MTag>
                    </label>
                  </li>
                );
              })}
            </ul>
            {visible.length < filtered.length && (
              <div className="text-center">
                <Button variant="secondary" onClick={onShowMore}>
                  {t('curatorGroups.showMore')}
                </Button>
              </div>
            )}
          </>
        )}
        {error && <p className="px-0.5 text-[13px] text-error">{error}</p>}
        {message && !error && <p className="px-0.5 text-[13px] text-success">{message}</p>}

        {/* Полоса выбора — над нижним меню (его высота + полоса жеста), только когда есть кого перекладывать. */}
        {selected.size > 0 && (
          <div
            data-selection-bar
            className="fixed inset-x-0 z-40 rounded-t-[22px] bg-surface-container-lowest px-4 pb-3 pt-3 shadow-[0_-4px_20px_rgba(36,31,27,0.12)]"
            style={{ bottom: 'calc(4.25rem + var(--sa-bottom))' }}
          >
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[13px] font-semibold text-on-surface">
                {t('curatorGroups.selectedCount', { count: selected.size })}
              </span>
              <button type="button" onClick={onClearSelection} className="min-h-11 px-2 text-[13px] font-semibold text-primary">
                {t('curatorGroups.clearSelection')}
              </button>
            </div>
            <select
              data-target-group
              value={targetGroupId}
              onChange={(e) => onTargetGroup(e.target.value)}
              className="app-field mb-2 h-11 w-full rounded-button bg-surface-container-low px-3 text-sm"
              aria-label={t('curatorGroups.pickGroup')}
            >
              {myGroups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
            <div className="grid grid-cols-2 gap-2">
              <Button disabled={!targetGroupId} loading={assigning} onClick={onAssign}>
                {t('curatorGroups.assignButton')}
              </Button>
              <Button variant="secondary" loading={removing} onClick={onRemove}>
                {t('curatorGroups.removeButton')}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <Card>
      <CardTitle>{t('curatorGroups.rosterTitle')}</CardTitle>

      <div className="mt-3 flex flex-wrap gap-2">
        {tabs.map((tb) => (
          <button
            key={tb.key}
            type="button"
            data-tab={tb.key}
            onClick={() => onTab(tb.key)}
            className={
              tb.key === tab
                ? 'rounded-pill bg-primary px-3 py-1.5 text-sm font-semibold text-on-primary'
                : 'rounded-pill bg-surface-container-lowest px-3 py-1.5 text-sm text-on-surface-variant'
            }
          >
            {tb.label}
          </button>
        ))}
      </div>

      <div className="mt-3">
        <Input
          data-roster-search
          placeholder={t('curatorGroups.searchPlaceholder')}
          value={query}
          onChange={(e) => onQuery(e.target.value)}
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-on-surface-variant">
        <span>{t('curatorGroups.shownOf', { shown: visible.length, total: filtered.length })}</span>
        <Button size="sm" variant="ghost" onClick={onSelectAll} disabled={!filtered.length}>
          {t('curatorGroups.selectAll')}
        </Button>
        {selected.size > 0 && (
          <>
            <span className="font-semibold text-on-surface">{t('curatorGroups.selectedCount', { count: selected.size })}</span>
            <Button size="sm" variant="ghost" onClick={onClearSelection}>
              {t('curatorGroups.clearSelection')}
            </Button>
          </>
        )}
      </div>

      {loading ? (
        <p className="mt-3 text-sm text-on-surface-variant">{t('common.loading')}</p>
      ) : !rows.length || !filtered.length ? (
        <p className="mt-3 text-sm text-on-surface-variant">{t('curatorGroups.rosterEmpty')}</p>
      ) : (
        <>
          <ul className="mt-3 space-y-2">
            {visible.map((r) => {
              const badge = rowBadge(r);
              const own = ownership(r, meId);
              return (
                <li
                  key={r.enrollment_id}
                  data-roster={r.enrollment_id}
                  className="flex items-start gap-2 rounded-card bg-surface-container-lowest px-3 py-2"
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 h-5 w-5 flex-none accent-primary"
                    checked={selected.has(r.enrollment_id)}
                    onChange={() => onToggle(r.enrollment_id)}
                    aria-label={r.student_name}
                  />
                  {/* ⚠️ ИМЯ — ОТДЕЛЬНЫМ ЯРУСОМ НА ТЕЛЕФОНЕ. Пока имя стояло в одном
                      переносящемся ряду с плашками, на 390 px оно сжималось до нуля
                      ширины: в кадре у учеников своей группы было видно чекбокс и
                      плашки, а имени не было вовсе. На широком экране ряд прежний. */}
                  <div className="min-w-0 flex-1 sm:flex sm:flex-wrap sm:items-center sm:gap-2">
                    <p className="truncate font-semibold text-on-surface sm:min-w-0 sm:flex-1">{r.student_name}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2 sm:mt-0">
                      {badge.kind === 'group' && (
                        <Pill variant="neutral">{t('curatorGroups.badgeGroup', { name: badge.name })}</Pill>
                      )}
                      {badge.kind === 'curator' && (
                        <Pill variant="neutral">{t('curatorGroups.badgePersonal', { name: badge.name })}</Pill>
                      )}
                      {badge.kind === 'none' && <Pill variant="neutral">{t('curatorGroups.badgeNoGroup')}</Pill>}
                      <Pill variant={own === 'mine' ? 'success' : own === 'free' ? 'primary' : 'warning'}>
                        {own === 'mine'
                          ? t('curatorGroups.ownMine')
                          : own === 'free'
                            ? t('curatorGroups.ownFree')
                            : t('curatorGroups.ownOther')}
                      </Pill>
                      {r.access_until && (
                        <span className="text-xs text-on-surface-variant">
                          {t('curatorGroups.accessUntil', { date: new Date(r.access_until).toLocaleDateString('ru-RU') })}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          {visible.length < filtered.length && (
            <div className="mt-3 text-center">
              <Button variant="secondary" onClick={onShowMore}>
                {t('curatorGroups.showMore')}
              </Button>
            </div>
          )}
        </>
      )}

      {/* Полоса действий появляется только когда есть кого перекладывать. */}
      {selected.size > 0 && (
        <div className="mt-4 flex flex-col gap-2 border-t border-outline-variant/40 pt-4 sm:flex-row sm:items-center">
          <select
            data-target-group
            value={targetGroupId}
            onChange={(e) => onTargetGroup(e.target.value)}
            className="app-field h-11 rounded-button bg-surface-container-lowest px-3 text-sm"
            aria-label={t('curatorGroups.pickGroup')}
          >
            {myGroups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
          <Button disabled={!targetGroupId} loading={assigning} onClick={onAssign}>
            {t('curatorGroups.assignButton')}
          </Button>
          <Button variant="secondary" loading={removing} onClick={onRemove}>
            {t('curatorGroups.removeButton')}
          </Button>
        </div>
      )}

      {error && <p className="mt-3 text-sm text-error">{error}</p>}
      {message && !error && <p className="mt-3 text-sm text-success">{message}</p>}
    </Card>
  );
}

/** Свой id — им отличается «мой личный ученик» от чужого (см. `curatorGroupRows`). */
function useMeId(): string | undefined {
  return useAuthStore((s) => s.user?.id);
}
